import {
  TASK_NOTIFY_CHANNEL_LABELS,
  proposedTaskNotifyChannel,
  taskNotifyFirstName,
  type TaskNotifyChannel,
  type TaskNotifyOptions,
} from '@cockpit/shared';
import { ref, shallowRef, watch, type Ref } from 'vue';
import { api } from './api';
import { errorText } from './composables/useResource';
import { useAuthStore } from './stores/auth';

/**
 * Lot AZ « Le prévenir » : canaux utilisables (chargés une fois, rechargés à la demande), brouillon du message
 * (prérempli tant que la personne ne l'a pas modifié), envoi et textes des toasts.
 */

const options = shallowRef<TaskNotifyOptions | null>(null);
let pending: Promise<void> | null = null;

/** Canaux utilisables par la personne connectée (`GET /api/me/task-notify`), partagés par tous les écrans. */
export function useTaskNotifyOptions(): {
  options: Ref<TaskNotifyOptions | null>;
  refresh: () => Promise<void>;
} {
  function refresh(): Promise<void> {
    pending ??= api
      .taskNotifyOptions()
      .then((value) => {
        options.value = value;
      })
      .catch(() => undefined)
      .finally(() => {
        pending = null;
      });
    return pending;
  }
  return { options, refresh };
}

/** Un canal est utilisable tant que le serveur n'a pas dit le contraire (sans réponse, le serveur tranchera). */
export const channelAvailable = (value: TaskNotifyOptions | null, channel: TaskNotifyChannel) =>
  value?.[channel].available ?? true;

/** Retient le canal choisi à l'écran (le serveur l'a enregistré à l'envoi). */
function rememberChannel(channel: TaskNotifyChannel): void {
  if (options.value) options.value = { ...options.value, lastChannel: channel };
  const auth = useAuthStore();
  if (auth.me) auth.me = { ...auth.me, preferences: { ...auth.me.preferences, taskNotifyChannel: channel } };
}

/**
 * Brouillon de « Prévenir » : case décochée (rien ne part sans elle), canal proposé (le dernier choisi, s'il est
 * utilisable), message prérempli qui suit le titre, la date et la personne tant qu'il n'a pas été modifié.
 */
export function useNotifyDraft(prefill: () => string) {
  const on = ref(false);
  const channel = ref<TaskNotifyChannel>('chat');
  const message = ref(prefill());
  let edited = false;
  watch(prefill, (value) => {
    if (!edited) message.value = value;
  });
  watch(
    options,
    (value) => {
      if (value) channel.value = proposedTaskNotifyChannel(value) ?? value.lastChannel;
    },
    { immediate: true },
  );
  function setMessage(value: string): void {
    message.value = value;
    edited = value !== prefill();
  }
  /** Après un ajout : case décochée, message prérempli de nouveau. */
  function reset(): void {
    on.value = false;
    edited = false;
    message.value = prefill();
  }
  return { on, channel, message, setMessage, reset };
}

export type NoticeOutcome = { ok: true; channel: TaskNotifyChannel } | { ok: false; error: string };

/** `POST /api/actions/:id/notify` : rend le résultat sans lever (la tâche, elle, est déjà enregistrée). */
export async function sendNotice(
  actionId: string,
  channel: TaskNotifyChannel,
  message: string,
): Promise<NoticeOutcome> {
  try {
    await api.notifyAction(actionId, { channel, message: message.trim() });
    rememberChannel(channel);
    return { ok: true, channel };
  } catch (err) {
    return { ok: false, error: errorText(err) };
  }
}

/** « message envoyé à Inès par Google Chat » */
export function sentText(name: string, channel: TaskNotifyChannel): string {
  const by = channel === 'chat' ? TASK_NOTIFY_CHANNEL_LABELS.chat : 'mail';
  return `message envoyé à ${taskNotifyFirstName(name)} par ${by}`;
}

/** Toast après « Envoyer » sur la ligne d'une carte passée à quelqu'un. */
export function handoffToast(name: string, outcome: NoticeOutcome): string {
  if (outcome.ok) {
    const text = sentText(name, outcome.channel);
    return `${text.charAt(0).toUpperCase()}${text.slice(1)}.`;
  }
  return `Carte passée à ${taskNotifyFirstName(name)}, mais le message n’est pas parti : ${outcome.error}`;
}

/** Toast après « Ajouter » avec « Prévenir » coché : `base` est le toast habituel de la tâche ajoutée. */
export function createdToast(base: string, name: string, outcome: NoticeOutcome): string {
  if (outcome.ok) return `${base} · ${sentText(name, outcome.channel)}`;
  return `Tâche ajoutée, mais le message n’est pas parti : ${outcome.error}`;
}
